import React, { useCallback, useMemo } from 'react';

import { FlowModal } from 'common/activeElements/modals/FlowModal';
import OverlappingBanner from 'common/OverlappingActivitiesBanner/OverlappingBanner';

import { breakdownFlowConfig } from './breakdownFlowConfig';
import CommonInputsStep from './CommonInputsStep';
import { NODE_IDS } from './const';
import BreakdownSummaryStep from './nodes/BreakdownSummaryStep';
import BusDeadheadStep from './nodes/BusDeadheadStep';
import DriverOutOfHoursReplacementStep from './nodes/DriverOutOfHoursReplacementStep';
import FatigueReplacementStep from './nodes/FatigueReplacementStep';
import ParallelRideStep from './nodes/ParallelRideStep';
import ParallelRideWithCushionStep from './nodes/ParallelRideWithCushionStep';
import { useCreateBreakdown } from './useCreateBreakdown';
import { buildSubmitPayload } from './utils/buildSubmitPayload';

/**
 * Wraps FlowModal with the breakdown flow's config + the assignment context.
 *
 * - Swaps the real components into every node of `breakdownFlowConfig`
 *   (the config keeps neutral node ids + validate rules).
 * - Seeds `initialData.assignment` so downstream steps can derive
 *   segment / breakdown-location options without prop-drilling.
 * - On summary confirmation, builds the mutation payload via
 *   `buildSubmitPayload`, invokes `useCreateBreakdown.submit`, and lets
 *   the hook drive Success / Error / Overlap branches.
 * - Renders the shared `<OverlappingBanner>` above the shell's Confirm
 *   button when the backend reports overlaps; Confirm is disabled until
 *   the user ticks "allow to delete overlaps" (or is permanently disabled
 *   with `cannotCancelOverlaps` copy when the backend keeps returning
 *   non-cancellable overlaps).
 * - Clears overlap state whenever the user navigates between steps —
 *   editing anything forces a fresh submit → fresh overlap check.
 *
 * @param {{
 *   assignment: Object,
 *   onSubmit: (breakdown: Object) => void,
 *   onClose: () => void,
 * }} props
 */
const CreateBreakdownFlow = ({ assignment, onSubmit, onClose }) => {
	const handleSuccess = useCallback(
		(breakdown) => {
			onSubmit?.(breakdown);
			onClose?.();
		},
		[onSubmit, onClose]
	);

	const {
		submit: submitBreakdown,
		loading: isSubmitting,
		overlappingList,
		hasOverlappingActivities,
		cannotCancelOverlaps,
		overlappingAction,
		isDelayOptionAvailable,
		allowToDeleteOverlaps,
		allowToDelayOverlaps,
		delayMinutes,
		delayError,
		minDelayMinutes,
		setDelayMinutes,
		setOverlappingAction,
		resetOverlaps,
	} = useCreateBreakdown({ onSuccess: handleSuccess });

	const flowConfig = useMemo(
		() => ({
			...breakdownFlowConfig,
			nodes: {
				...breakdownFlowConfig.nodes,
				[NODE_IDS.common]: {
					...breakdownFlowConfig.nodes[NODE_IDS.common],
					component: CommonInputsStep,
				},
				[NODE_IDS.parallelRide]: {
					...breakdownFlowConfig.nodes[NODE_IDS.parallelRide],
					component: ParallelRideStep,
				},
				[NODE_IDS.parallelRideWithCushion]: {
					...breakdownFlowConfig.nodes[NODE_IDS.parallelRideWithCushion],
					component: ParallelRideWithCushionStep,
				},
				[NODE_IDS.busDeadhead]: {
					...breakdownFlowConfig.nodes[NODE_IDS.busDeadhead],
					component: BusDeadheadStep,
				},
				[NODE_IDS.fatigueReplacement]: {
					...breakdownFlowConfig.nodes[NODE_IDS.fatigueReplacement],
					component: FatigueReplacementStep,
				},
				[NODE_IDS.driverOutOfHoursReplacement]: {
					...breakdownFlowConfig.nodes[NODE_IDS.driverOutOfHoursReplacement],
					component: DriverOutOfHoursReplacementStep,
				},
				[NODE_IDS.summary]: {
					...breakdownFlowConfig.nodes[NODE_IDS.summary],
					component: BreakdownSummaryStep,
				},
			},
		}),
		[]
	);

	const handleSubmit = useCallback(
		(data, submitMeta) => {
			const payload = buildSubmitPayload(data, {
				sequenceGroupId: assignment?.sequenceGroupId,
				originalDriverId: assignment?.crewMember?.id,
				previousNodeId: submitMeta?.previousNodeId,
			});
			submitBreakdown(payload);
		},
		[assignment, submitBreakdown]
	);

	const bannerSlot = hasOverlappingActivities ? (
		<OverlappingBanner
			overlappingList={overlappingList}
			overlappingAction={overlappingAction}
			onOverlappingActionChange={setOverlappingAction}
			cannotCancelOverlaps={cannotCancelOverlaps}
			isDelayOptionAvailable={isDelayOptionAvailable}
			delayMinutes={delayMinutes}
			onDelayMinutesChange={setDelayMinutes}
			delayError={delayError}
			minDelayMinutes={minDelayMinutes}
		/>
	) : null;

	const isConfirmDisabled =
		(hasOverlappingActivities &&
			(cannotCancelOverlaps || (!allowToDeleteOverlaps && !allowToDelayOverlaps))) ||
		!!delayError;

	return (
		<FlowModal
			flowConfig={flowConfig}
			initialData={{ assignment }}
			onSubmit={handleSubmit}
			onClose={onClose}
			bannerSlot={bannerSlot}
			isConfirmDisabled={isConfirmDisabled}
			isConfirmLoading={isSubmitting}
			onNavigate={resetOverlaps}
		/>
	);
};

export default CreateBreakdownFlow;
